import React from "react";

import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import GroupsIcon from "@mui/icons-material/Groups";
import ShareIcon from "@mui/icons-material/Share";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import StarBorderPurple500Icon from "@mui/icons-material/StarBorderPurple500";
import SyncIcon from "@mui/icons-material/Sync";

import { Language } from "./DestinationModal.types";

const DESTINATIONS_WITHOUT_STATIC_OVERVIEW = new Set(["fbca", "ttca"]);

interface BaseOverviewContentProps {
    language?: Language;
    code?: string;
}

export const getBaseOverviewContent = ({
    language,
    code,
}: BaseOverviewContentProps) => {
    const showStaticOverview = !DESTINATIONS_WITHOUT_STATIC_OVERVIEW.has(
        code ?? "",
    );

    return (
        <>
            {showStaticOverview && (
                <>
                    <p className="mb-3">
                        {language?.tracker_detail?.overview?.message ??
                            "Signalsight.io seamlessly integrates a diverse array of data sources and destinations, enabling comprehensive and real-time data flow across your marketing and sales platforms. From website interactions and offline events to CRM systems and mobile apps, our platform ensures accurate data synchronization, empowering you to make data-driven decisions. With robust support for major advertising channels, including Meta, TikTok, Snap, and Pinterest, Signalsight.io optimizes your campaigns and enhances your customer insights for superior marketing performance."}
                    </p>
                    <div className="source-detail-modal-card mb-3">
                        <div className="row">
                            <div className="col-md-6 col-sm-12 source-detail-modal-card-border-right">
                                <h2>
                                    {language?.tracker_detail?.overview
                                        ?.data_point_overview ??
                                        "Data Point Overview"}
                                </h2>
                                <span className="title">
                                    <ShareIcon></ShareIcon>{" "}
                                    {language?.tracker_detail?.overview
                                        ?.destination ?? "Destination"}
                                </span>
                                <p className="mb-2">
                                    {language?.tracker_detail?.overview
                                        ?.destination_msg ??
                                        "Your target endpoint where data is sent for analysis and action, such as ad platforms or marketing analytics tools."}
                                </p>
                                <span className="title">
                                    <StarBorderPurple500Icon></StarBorderPurple500Icon>{" "}
                                    {language?.tracker_detail?.overview
                                        ?.features ?? "Features"}
                                </span>
                                <p className="mb-1">
                                    {language?.tracker_detail?.overview
                                        ?.feature_1 ??
                                        "-Real time event transmission"}
                                </p>
                                <p>
                                    {language?.tracker_detail?.overview
                                        ?.feature_2 ?? "-No data storing"}
                                </p>
                            </div>
                            <div className="col-md-6 col-sm-12">
                                <h2>
                                    {language?.tracker_detail?.overview
                                        ?.setup_requirements ??
                                        "Set up Requirements"}
                                </h2>
                                <p className="mb-2">
                                    {language?.tracker_detail?.overview
                                        ?.destination_setup_requirements_1 ??
                                        "There will be a minimum of 2 node points to connect with."}
                                </p>
                                <p>
                                    {language?.tracker_detail?.overview
                                        ?.destination_setup_requirements_2 ??
                                        "These are referred to as the Source and the Destination."}
                                </p>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </>
    );
};

const getTiktokCustomAudienceOverview = (language?: Language) => {
    const overview = language?.tracker_detail?.overview;

    return (
        <div>
            <p className="mb-3">
                {overview?.ttca_msg ??
                    "Build and manage Custom Audiences on TikTok by syncing your first-party customer data. Match your existing contacts against TikTok's user base to reach high-intent people, power lookalike modeling, and keep your targeting segments accurate as your data changes."}
            </p>

            <div className="source-detail-modal-card mb-3">
                <div className="row">
                    <div className="col-md-6 col-sm-12 source-detail-modal-card-border-right">
                        <h2>{overview?.ttca_how_it_works ?? "How it works"}</h2>

                        <span className="title">
                            <SyncIcon />{" "}
                            {overview?.ttca_sync_title ?? "Automatic sync"}
                        </span>
                        <p className="mb-2">
                            {overview?.ttca_sync_msg ??
                                "Customer data flows from your source to TikTok and keeps your audiences refreshed in near real time."}
                        </p>

                        <span className="title">
                            <ShieldOutlinedIcon />{" "}
                            {overview?.ttca_privacy_title ??
                                "Privacy-safe matching"}
                        </span>
                        <p className="mb-2">
                            {overview?.ttca_privacy_msg ??
                                "Identifiers such as emails and phone numbers are hashed before they are sent, so raw personal data never leaves your control."}
                        </p>

                        <span className="title">
                            <GroupsIcon />{" "}
                            {overview?.ttca_audience_title ??
                                "Reusable audiences"}
                        </span>
                        <p>
                            {overview?.ttca_audience_msg ??
                                "Create a new Custom Audience or append to an existing one, then reuse it across your TikTok ad campaigns."}
                        </p>
                    </div>

                    <div className="col-md-6 col-sm-12">
                        <h2>
                            {overview?.ttca_use_cases ?? "Common use cases"}
                        </h2>

                        <span className="title">
                            <CampaignOutlinedIcon />{" "}
                            {overview?.ttca_use_case_retarget_title ??
                                "Retarget & re-engage"}
                        </span>
                        <p className="mb-2">
                            {overview?.ttca_use_case_retarget_msg ??
                                "Reconnect with existing customers and win back users who have lapsed."}
                        </p>

                        <span className="title">
                            <GroupsIcon />{" "}
                            {overview?.ttca_use_case_lookalike_title ??
                                "Lookalike targeting"}
                        </span>
                        <p className="mb-2">
                            {overview?.ttca_use_case_lookalike_msg ??
                                "Use your best customers as a seed to help TikTok find similar high-value audiences."}
                        </p>

                        <h2 className="mt-3">
                            {overview?.setup_requirements ??
                                "Set up Requirements"}
                        </h2>
                        <p className="mb-2">
                            {overview?.ttca_setup_1 ??
                                "Authorize your TikTok account and pick the ad account that owns the audience."}
                        </p>
                        <p>
                            {overview?.ttca_setup_2 ??
                                "Select an existing Custom Audience or create a new one, then map the customer fields you want to sync."}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export const getDestinationSpecificContent = (
    code: string,
    language?: Language,
) => {
    if (code === "ttca") {
        return getTiktokCustomAudienceOverview(language);
    }

    const contentMap: Record<string, { title: string; description: string }> = {
        fbpixel: {
            title: "Meta Pixel",
            description:
                language?.tracker_detail?.overview?.fbpixel_msg ??
                "Track and analyze user interactions on your website with Meta Pixel. This destination provides insights for optimizing your Facebook ads and campaigns. It supports detailed event tracking, such as page views, purchases, and form submissions. Meta Pixel helps you understand user behavior and improve ad targeting.",
        },
        alpixel: {
            title: "Applovin Pixel",
            description:
                language?.tracker_detail?.overview?.alpixel_msg ??
                "Track and analyze user interactions on your website with Meta Pixel. This destination provides insights for optimizing your Facebook ads and campaigns. It supports detailed event tracking, such as page views, purchases, and form submissions. Meta Pixel helps you understand user behavior and improve ad targeting.",
        },
        fbcapi: {
            title: "Meta Conversions API",
            description:
                language?.tracker_detail?.overview?.fbcapi_msg ??
                "Send conversion data directly to Meta for improved ad performance and targeting. This destination supports server-to-server data sharing for better accuracy. It enables real-time conversion tracking, ensuring up-to-date insights. Meta Conversions API helps you measure the effectiveness of your Facebook ads with precision.",
        },
        fboffline: {
            title: "Meta Offline Conversions",
            description:
                language?.tracker_detail?.overview?.fboffline_msg ??
                "Upload offline event data to Meta to measure the impact of your Facebook ads. This destination helps bridge online and offline customer interactions. It supports batch uploads, making it easy to manage large datasets. Meta Offline Conversions provides a complete view of your marketing performance.",
        },

        ttpixel: {
            title: "TikTok Pixel",
            description:
                language?.tracker_detail?.overview?.ttpixel_msg ??
                "Track user behavior on your website with the TikTok Pixel. This destination provides valuable insights for optimizing your TikTok ad campaigns. It supports event tracking for actions like page views, purchases, and sign-ups. TikTok Pixel helps you refine your ad strategies based on user interactions.",
        },
        ttcapi: {
            title: "TikTok Conversions API",
            description:
                language?.tracker_detail?.overview?.ttcapi_msg ??
                "Send web event data directly to TikTok for enhanced ad targeting and measurement. This destination ensures accurate tracking of user interactions. It supports real-time data transfer, providing immediate insights. TikTok Web Events API helps you optimize your marketing efforts with precise data.",
        },
        ttoffline: {
            title: "TikTok Offline Events",
            description:
                language?.tracker_detail?.overview?.ttoffline_msg ??
                "Upload offline event data to TikTok to measure the effectiveness of your ad campaigns. This destination helps connect offline actions with online marketing efforts. It supports bulk data uploads, ensuring efficient data management. TikTok Offline Events provides a holistic view of your campaign performance.",
        },
        scpixel: {
            title: "Snapchat Pixel",
            description:
                language?.tracker_detail?.overview?.scpixel_msg ??
                "Monitor user interactions on your website using the Snap Pixel. This destination helps optimize your Snapchat advertising strategies. It supports event tracking for activities like page views, purchases, and form submissions. Snap Pixel provides insights to enhance your ad targeting and performance.",
        },
        sccapi: {
            title: "Snapchat Conversions API",
            description:
                language?.tracker_detail?.overview?.sccapi_msg ??
                "Send conversion data directly to Snapchat for improved ad performance. This destination supports accurate event tracking and targeting. It enables real-time data transfer, ensuring up-to-date insights. Snap Conversions API helps you measure and optimize the impact of your Snapchat ads.",
        },
        pnpixel: {
            title: "Pinterest Tag",
            description:
                language?.tracker_detail?.overview?.pnpixel_msg ??
                "Track user behavior on your website with the Pinterest Tag. This destination helps you analyze and improve your Pinterest marketing campaigns. It supports event tracking for actions like page views, sign-ups, and purchases. Pinterest Tag provides valuable insights to enhance your ad strategies.",
        },
        pncapi: {
            title: "Pinterest Conversion API",
            description:
                language?.tracker_detail?.overview?.pncapi_msg ??
                "Send conversion data directly to Pinterest for better ad targeting and measurement. This destination ensures precise tracking of user actions. It supports server-to-server data transfer, providing real-time insights. Pinterest Conversion API helps you optimize your campaigns with accurate conversion data.",
        },
        ggads: {
            title: "Google Offline Conversions",
            description:
                language?.tracker_detail?.overview?.ggads_msg ??
                "Upload offline event data to Google to measure the impact of your online ads. This destination helps bridge the gap between offline interactions and online marketing. It supports bulk data uploads, ensuring efficient data management. Google Offline Conversions provides a comprehensive view of your ad performance.",
        },
        ggads_web: {
            title: "Google Web Conversions",
            description:
                language?.tracker_detail?.overview?.ggads_msg ??
                "Upload offline event data to Google to measure the impact of your online ads. This destination helps bridge the gap between offline interactions and online marketing. It supports bulk data uploads, ensuring efficient data management. Google Offline Conversions provides a comprehensive view of your ad performance.",
        },
        ggawc: {
            title: "Google Ads Website Conversion",
            description:
                language?.tracker_detail?.overview?.ggawc_msg ??
                "Send website conversions to Google Ads for conversion actions that were set up with the Google Tag or Google Tag Manager. Server events are matched with the Google Tag events inside the same conversion action through the transaction id and deduplicated, so the client-side event must send the same id. Conversion actions imported from GA4 or based on URL rules are not supported.",
        },
        ggaoc: {
            title: "Google Ads Offline Conversion",
            description:
                language?.tracker_detail?.overview?.ggaoc_msg ??
                "Upload offline conversions to Google Ads conversion actions that accept offline click uploads, so purchases and other events that happen after the click can be attributed to your campaigns.",
        },
        ggalc: {
            title: "Google Ads Lead Conversion",
            description:
                language?.tracker_detail?.overview?.ggalc_msg ??
                "Upload qualified lead events to Google Ads conversion actions that accept offline click uploads, then map each SignalSight event to its Google Ads conversion action.",
        },
        ggass: {
            title: "Google Ads Store Sales",
            description:
                language?.tracker_detail?.overview?.ggass_msg ??
                "Send in-store purchase events to eligible Google Ads Store Sales conversion actions and map each SignalSight event to its Google Ads conversion action.",
        },
        twcapi: {
            title: "X Conversion API",
            description:
                language?.tracker_detail?.overview?.twcapi_msg ??
                "Send conversion data directly to X for improved ad performance and targeting. This destination supports server-to-server data sharing for accuracy. It enables real-time conversion tracking, providing immediate insights. X Conversion API helps you measure the effectiveness of your ads with precision.",
        },
        twpixel: {
            title: "X Pixel",
            description:
                language?.tracker_detail?.overview?.twpixel_msg ??
                "Track and analyze user interactions on your website with X Pixel. This destination provides insights for optimizing your ad campaigns on the X platform. It supports event tracking for activities like page views, purchases, and sign-ups. X Pixel helps you understand user behavior and improve ad targeting.",
        },
        oaipixel: {
            title: "OpenAI Ads Pixel",
            description:
                "Send browser events to OpenAI Ads with a Pixel ID. This destination does not use an access token or event mapping.",
        },
        oaicapi: {
            title: "OpenAI Ads Conversions API",
            description:
                "Send server-side conversions to OpenAI Ads. Map each internal event to an OpenAI Ads conversion.",
        },
        ymwc: {
            title: "Yandex Metrica Web Conversions",
            description:
                language?.tracker_detail?.overview?.ymwc_msg ??
                "Connect a Yandex account and send web conversions to the selected Yandex Metrica counter.",
        },
    };

    const content = contentMap[code];
    if (!content) return null;

    return (
        <div>
            <h1
                style={{
                    fontWeight: 600,
                }}
            >
                {content.title}
            </h1>
            <p>{content.description}</p>
        </div>
    );
};
